import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { Observable, Subject, filter, firstValueFrom, lastValueFrom, map, takeUntil, timeout } from 'rxjs';
import { SESSION_STORAGE_KEYS } from '../session_storage/constants/session-storage-keys.constant';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { UserDTO } from 'app/core/api/models/userDTO';
import { SCOPES } from '../authorization/constants/scopes.constant';
import { CLOCK_SKEW_BUFFER_SECONDS } from './constants/clock-skew.constant';
import { OrganizationContextService } from './organization-context.service';
import {
  clearStoredAccountSelection,
  persistStoredAccountSelection,
  resolveAccountForTenant,
} from './account-selection.util';
import { AuthSwitchAttempt } from './auth-switch-attempt';

export type TenantSwitchResult = 'silent' | 'redirect';

@Injectable({
  providedIn: 'root',
})
export class AuthUserService {
  private readonly _destroying$ = new Subject<void>();

  isFrame = false;

  loginDisplay = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private http: HttpClient,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private organizationContext: OrganizationContextService,
  ) {
    this.isFrame = window !== window.parent && !window.opener;
  }

  async initialize(): Promise<void> {
    await this.authService.instance.initialize();
    this.setLoginDisplay();
    this.authService.instance.enableAccountStorageEvents();
  }

  setLoginDisplay(): void {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount(): void {
    const activeAccount = this.authService.instance.getActiveAccount();
    const resolvedAccount = resolveAccountForTenant(
      this.authService.instance.getAllAccounts(),
      activeAccount,
      localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID),
    );
    if (
      activeAccount?.tenantId !== resolvedAccount?.tenantId ||
      activeAccount?.homeAccountId !== resolvedAccount?.homeAccountId
    ) {
      this.authService.instance.setActiveAccount(resolvedAccount);
    }
    persistStoredAccountSelection(resolvedAccount);
  }

  getStatus(): Observable<string> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this._destroying$),
    );
  }

  loginRedirect(): void {
    // Check if the user is already logged in with valid tokens
    const accounts = this.authService.instance.getAllAccounts();

    if (accounts.length > 0) {
      // Verify at least one account has valid (non-expired) claims
      const hasValidAccount = accounts.some((acc) => {
        // Check basic claims exist
        if (!acc.idTokenClaims || !acc.tenantId) {
          return false;
        }
        // Check token expiry
        const exp = acc.idTokenClaims['exp'] as number | undefined;
        if (exp) {
          const nowInSeconds = Math.floor(Date.now() / 1000);
          return exp > nowInSeconds + CLOCK_SKEW_BUFFER_SECONDS;
        }
        return true; // No exp claim = assume valid
      });

      if (hasValidAccount) {
        this.loginDisplay = true;
        return;
      }
    }

    this.loginDisplay = false;

    if (this.hasInteractionInProgressFlag()) {
      console.warn('AuthUserService.loginRedirect skipped: MSAL interaction already in progress');
      return;
    }

    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
        redirectStartPage: window.location.href,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect({
        scopes: [SCOPES.USER_IMPERSONATION],
        redirectStartPage: window.location.href,
      });
    }
  }

  private hasInteractionInProgressFlag(): boolean {
    const storages = [sessionStorage, localStorage];
    for (const storage of storages) {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key || !key.includes('interaction.status')) {
          continue;
        }
        if (storage.getItem(key) === 'interaction_in_progress') {
          return true;
        }
      }
    }
    return false;
  }

  async switchToAnotherTenant(newTenant: string): Promise<TenantSwitchResult> {
    const attempt = new AuthSwitchAttempt(this.authService, this.organizationContext, newTenant);
    try {
      attempt.selectTenant(newTenant);
      const authority = `https://login.microsoftonline.com/${newTenant}/`;

      // First, check if we already have an account for this tenant
      const allAccounts = this.authService.instance.getAllAccounts();
      const accountForTenant = resolveAccountForTenant(
        allAccounts,
        this.authService.instance.getActiveAccount(),
        newTenant,
      );

      if (accountForTenant) {
        // We have an account for this tenant, set it as active and try silent token acquisition
        attempt.selectAccount(accountForTenant);

        try {
          const result = await firstValueFrom(
            this.authService
              .acquireTokenSilent({
                scopes: [SCOPES.USER_IMPERSONATION],
                account: accountForTenant,
                authority: authority,
                forceRefresh: true,
              })
              .pipe(timeout(10000)),
          );
          attempt.assertCurrent(accountForTenant);
          if (
            !result.account ||
            result.account.tenantId !== newTenant ||
            result.account.homeAccountId !== accountForTenant.homeAccountId
          ) {
            throw new Error('Token account does not match the selected portal context');
          }
          attempt.selectAccount(result.account);
          this.organizationContext.confirm(result.account);
          return 'silent';
        } catch (silentError) {
          // A different tab or logout owns the newer context; never overwrite it
          // or launch a redirect for the now-obsolete target.
          attempt.assertCurrent(accountForTenant);
          console.warn('Silent token acquisition for tenant switch failed; attempting redirect to re-authenticate');
        }
      }

      // No account for this tenant or silent acquisition failed - need to redirect
      // Use acquireTokenRedirect with the correct authority to get a token for the new tenant
      // Observe the Angular wrapper's Observable, including failures before navigation.
      attempt.clearSelection(newTenant);
      await lastValueFrom(
        this.authService.acquireTokenRedirect({
          scopes: [SCOPES.USER_IMPERSONATION],
          authority: authority,
          prompt: 'select_account', // Allow user to select account for the new tenant
          redirectStartPage: window.location.href,
        }),
        { defaultValue: undefined },
      );
      attempt.assertCurrent();
      return 'redirect';
    } catch (error) {
      attempt.recover();
      throw error;
    }
  }

  logout(): void {
    this.organizationContext.clear();
    clearStoredAccountSelection(localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID));
    localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
    localStorage.removeItem('currentUser');
    this.authService.logout();
  }

  getAadProfilePicture(): Observable<any> {
    return this.http.get('https://graph.microsoft.com/v1.0/me/photo/$value', {
      responseType: 'blob',
      headers: new HttpHeaders({ 'Content-Type': 'image/jpeg' }),
    });
  }

  getAadProfilePictureForUser(user: UserDTO): Observable<any> {
    if (user !== null && typeof user !== 'undefined') {
      let srcString: string = user.profilePicture.toString();
      return this.http
        .get(srcString, {
          responseType: 'blob',
          headers: new HttpHeaders({ 'Content-Type': 'image/jpeg' }),
        })
        .pipe(
          map((res: any) => {
            return res;
          }),
        );
    }
  }

  destroy(): void {
    this._destroying$.next(null);
    this._destroying$.complete();
  }
}
